import React from "react"
import {Box, Button} from "@mui/material"
import {ArrowBackRounded} from "@mui/icons-material"
import {Link} from "react-router-dom"
import PageHeader from "./PageHeader"
import {packagingTokens as tk} from "@/theme/packagingTokens"
import useIframeFloorHeight from "@/utils/useIframeFloorHeight"

type BuilderShellProps = {
    backLabel: string
    backTo?: string
    onBack?: () => void
    title: string
    subtitle?: React.ReactNode
    actions?: React.ReactNode
    maxWidth?: number
    children: React.ReactNode
}

// Full-page chrome for the create/edit "builder" routes (which render outside the
// sidebar shell). A slim top bar with a Back button (+ optional actions), then the
// Rémi page header and the form content. Matches the app's look without the rail.
export default function BuilderShell(props: BuilderShellProps) {
    const {backLabel, backTo, onBack, title, subtitle, actions, maxWidth = 920, children} = props
    const floorHeight = useIframeFloorHeight()

    const backButton = (
        <Button
            {...(onBack ? {onClick: onBack} : {component: Link, to: backTo})}
            startIcon={<ArrowBackRounded sx={{fontSize: 18}} />}
            sx={{
                textTransform: "none",
                fontSize: "13.5px",
                fontWeight: 600,
                color: tk.color.textSecondary,
                borderRadius: tk.radius.sm,
                px: 1.25,
                "&:hover": {backgroundColor: tk.color.bg, color: tk.color.textPrimary},
            }}
        >
            {backLabel}
        </Button>
    )

    return (
        <Box sx={{minHeight: floorHeight, backgroundColor: tk.color.bg}}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    px: {xs: 1.5, sm: 4},
                    height: tk.layout.headerHeight,
                    backgroundColor: tk.color.surface,
                    borderBottom: `1px solid ${tk.color.border}`,
                }}
            >
                {backButton}
                {actions && <Box sx={{display: "flex", gap: 1}}>{actions}</Box>}
            </Box>

            <Box sx={{p: {xs: 2, sm: 4}, maxWidth, mx: "auto"}}>
                <PageHeader title={title} subtitle={subtitle} />
                {children}
            </Box>
        </Box>
    )
}
